import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CircularProgress,
  Chip,
  alpha,
  Paper
} from '@mui/material';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import SchoolIcon from '@mui/icons-material/School';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const API_URL = process.env.REACT_APP_API_URL || '';

function CandidateCourses() {
  const [tab, setTab] = useState('available');
  const [availableCourses, setAvailableCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [courseDetail, setCourseDetail] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [assessmentQuestions, setAssessmentQuestions] = useState([]);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [resultOpen, setResultOpen] = useState(false);
  const [result, setResult] = useState({ score: 0, passed: false, certificate_url: null });
  const [completeWithoutModal, setCompleteWithoutModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assessmentError, setAssessmentError] = useState('');
  const [videoDetailLoading, setVideoDetailLoading] = useState(false);
  const [videoQuestions, setVideoQuestions] = useState([]);
  const [videoQuizAnswers, setVideoQuizAnswers] = useState({});
  const [videoQuizScore, setVideoQuizScore] = useState(null);
  const [videoQuizPassed, setVideoQuizPassed] = useState(false);
  const [videoQuizSubmitting, setVideoQuizSubmitting] = useState(false);
  const [videoPassingScore, setVideoPassingScore] = useState(70);
  const [videoSnack, setVideoSnack] = useState('');

  const token = localStorage.getItem('gogig_token');
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const fetchAvailable = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/courses/courses`, authHeaders());
      const data = await res.json();
      if (data.success && data.courses) setAvailableCourses(data.courses);
      else setAvailableCourses([]);
    } catch (e) {
      setAvailableCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyCourses = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/courses/my-courses`, authHeaders());
      const data = await res.json();
      if (data.success && data.enrollments) setEnrollments(data.enrollments);
      else setEnrollments([]);
    } catch (e) {
      setEnrollments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'available') fetchAvailable();
    if (tab === 'my-courses') fetchMyCourses();
  }, [tab]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/courses/my-courses`, authHeaders());
        const data = await res.json();
        if (!cancelled && data.success && data.enrollments && data.enrollments.length > 0) {
          setEnrollments(data.enrollments);
          setTab('my-courses');
        }
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchCourseDetail = async (courseId) => {
    setLoading(true);
    setCourseDetail(null);
    try {
      const res = await fetch(`${API_URL}/api/courses/courses/${courseId}`, authHeaders());
      const data = await res.json();
      if (data.success) {
        setCourseDetail({
          ...data.course,
          chapters: data.chapters || [],
          enrollment: data.enrollment,
          totalVideos: data.totalVideos ?? 0,
          completedVideos: data.completedVideos ?? 0,
          has_assessment: data.course?.has_assessment,
          passing_score: data.course?.passing_score
        });
      }
    } catch (e) {
      setCourseDetail(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (courseId) => {
    try {
      const res = await fetch(`${API_URL}/api/courses/courses/${courseId}/enroll`, { method: 'POST', ...authHeaders() });
      const data = await res.json();
      if (data.success) {
        fetchMyCourses();
        fetchCourseDetail(courseId);
        setTab('my-courses');
      }
    } catch (e) {}
  };

  const loadVideoDetail = async (courseId, video) => {
    setSelectedVideo({ ...video, courseId });
    setVideoDetailLoading(true);
    setVideoQuestions([]);
    setVideoQuizAnswers({});
    setVideoQuizScore(video.quizScore ?? null);
    setVideoQuizPassed(!!video.quizPassed);
    setVideoSnack('');
    try {
      const res = await fetch(`${API_URL}/api/courses/courses/${courseId}/videos/${video.id}`, authHeaders());
      const data = await res.json();
      if (data.success) {
        setVideoQuestions(data.questions || []);
        setVideoPassingScore(data.passing_score ?? 70);
        if (data.video) {
          setSelectedVideo((prev) => ({
            ...prev,
            ...data.video,
            courseId,
            hasQuiz: data.video.hasQuiz ?? (data.questions || []).length > 0,
            quizPassed: data.video.quizPassed,
            quizScore: data.video.quizScore,
            completed: data.video.completed
          }));
          setVideoQuizPassed(!!data.video.quizPassed);
          setVideoQuizScore(data.video.quizScore ?? null);
        }
      }
    } catch (e) {
      setVideoSnack('Could not load video details.');
    } finally {
      setVideoDetailLoading(false);
    }
  };

  const handleSubmitVideoQuiz = async () => {
    if (!selectedVideo || !courseDetail) return;
    if (videoQuestions.length > 0 && Object.keys(videoQuizAnswers).length < videoQuestions.length) {
      setVideoSnack('Please answer all questions.');
      return;
    }
    setVideoQuizSubmitting(true);
    setVideoSnack('');
    try {
      const res = await fetch(
        `${API_URL}/api/courses/courses/${courseDetail.id}/videos/${selectedVideo.id}/submit-quiz`,
        {
          method: 'POST',
          ...authHeaders(),
          headers: { ...authHeaders().headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            answers: Object.fromEntries(
              Object.entries(videoQuizAnswers).map(([k, v]) => [k, v ? parseInt(v, 10) : null])
            )
          })
        }
      );
      const data = await res.json();
      if (data.success) {
        setVideoQuizScore(data.score);
        setVideoQuizPassed(!!data.passed);
        setVideoSnack(data.passed
          ? `Quiz passed (${data.score}%). You can mark the video complete.`
          : `Score ${data.score}%. Need ${data.passing_score ?? videoPassingScore}% to continue. Try again.`);
        fetchCourseDetail(courseDetail.id);
      } else {
        setVideoSnack(data.message || 'Quiz submit failed.');
      }
    } catch (e) {
      setVideoSnack('Network error submitting quiz.');
    }
    setVideoQuizSubmitting(false);
  };

  const handleMarkComplete = async (courseId, videoId) => {
    try {
      const res = await fetch(`${API_URL}/api/courses/courses/${courseId}/videos/${videoId}/complete`, {
        method: 'POST',
        ...authHeaders(),
        headers: { ...authHeaders().headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setVideoSnack(data.message || 'Could not mark complete. Pass the video quiz first if required.');
        return;
      }
      if (selectedVideo && String(selectedVideo.id) === String(videoId)) {
        setSelectedVideo((prev) => (prev ? { ...prev, completed: true } : prev));
      }
      if (courseDetail && courseDetail.id === parseInt(courseId, 10)) fetchCourseDetail(courseId);
    } catch (e) {
      setVideoSnack('Network error.');
    }
  };

  const handleStartAssessment = async () => {
    if (!courseDetail) return;
    setAssessmentError('');
    setAssessmentLoading(true);
    setAssessmentQuestions([]);
    setAssessmentAnswers({});
    try {
      const res = await fetch(`${API_URL}/api/courses/courses/${courseDetail.id}/start-assessment`, {
        method: 'POST',
        ...authHeaders(),
        headers: { ...authHeaders().headers, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        const qs = data.questions || [];
        setAssessmentQuestions(qs);
        setAssessmentOpen(true);
        if (qs.length === 0) setAssessmentError('No assessment questions are configured. You can complete without assessment below.');
      } else {
        setAssessmentError(data.message || 'Could not load assessment. You can complete without assessment below.');
      }
    } catch (e) {
      setAssessmentError('Network error. Please try again.');
    } finally {
      setAssessmentLoading(false);
    }
  };

  const handleSubmitAssessment = async () => {
    if (!courseDetail) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/courses/courses/${courseDetail.id}/submit-assessment`, {
        method: 'POST',
        ...authHeaders(),
        headers: { ...authHeaders().headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: Object.fromEntries(Object.entries(assessmentAnswers).map(([k, v]) => [k, v ? parseInt(v, 10) : null])) })
      });
      const data = await res.json();
      if (data.success) {
        setAssessmentOpen(false);
        setResult({ score: data.score, passed: data.passed, certificate_url: data.certificate_url || null });
        setResultOpen(true);
        fetchCourseDetail(courseDetail.id);
        fetchMyCourses();
      }
    } catch (e) {}
    setSubmitting(false);
  };

  const handleCompleteCourse = async () => {
    if (!courseDetail) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/courses/courses/${courseDetail.id}/complete`, {
        method: 'POST',
        ...authHeaders(),
        headers: { ...authHeaders().headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.success) {
        setCompleteWithoutModal(false);
        if (data.certificate_url) {
          setResult({ score: 100, passed: true, certificate_url: data.certificate_url });
          setResultOpen(true);
        }
        fetchCourseDetail(courseDetail.id);
        fetchMyCourses();
      } else {
        setAssessmentError(data.message || 'Could not complete course.');
      }
    } catch (e) {
      setAssessmentError('Network error.');
    }
    setSubmitting(false);
  };

  const handleDownloadCertificate = async (url) => {
    if (!url || !token) return;
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    try {
      const res = await fetch(fullUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const w = window.open(blobUrl, '_blank');
      if (w) setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (e) {}
  };

  const progress = courseDetail && courseDetail.totalVideos > 0
    ? Math.round((courseDetail.completedVideos / courseDetail.totalVideos) * 100)
    : (courseDetail?.enrollment?.progress_percentage ?? 0);
  const is100 = progress >= 100;
  const isCompleted = courseDetail?.enrollment?.status === 'completed';

  return (
    <Box sx={{ width: '100%', maxWidth: 960, margin: '0 auto', px: { xs: 2, sm: 3 }, py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <SchoolIcon sx={{ fontSize: 36, color: '#0f766e' }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f766e' }}>Training Courses</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Enroll in courses, watch videos, and pass the assessment to earn your certificate.</Typography>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 1, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', display: 'inline-flex', gap: 0.5 }}>
        <Button variant={tab === 'available' ? 'contained' : 'text'} size="small" onClick={() => setTab('available')} sx={{ textTransform: 'none', borderRadius: 1.5, minWidth: 140, fontWeight: 600, bgcolor: tab === 'available' ? '#0f766e' : undefined, '&:hover': { bgcolor: tab === 'available' ? '#0d9488' : undefined } }}>Available</Button>
        <Button variant={tab === 'my-courses' ? 'contained' : 'text'} size="small" onClick={() => setTab('my-courses')} sx={{ textTransform: 'none', borderRadius: 1.5, minWidth: 140, fontWeight: 600, bgcolor: tab === 'my-courses' ? '#0f766e' : undefined, '&:hover': { bgcolor: tab === 'my-courses' ? '#0d9488' : undefined } }}>My Courses</Button>
      </Paper>

      {tab === 'available' && (
        <Box>
          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
          {!loading && availableCourses.length === 0 && (
            <Card sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
              <VideoLibraryIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary" variant="body1">No courses available right now.</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Check back later or contact your admin.</Typography>
            </Card>
          )}
          {!loading && availableCourses.map((c) => (
            <Card key={c.id} sx={{ p: 2.5, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', '&:hover': { borderColor: 'primary.main', boxShadow: 1 } }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={600}>{c.title}</Typography>
                  {c.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{c.description}</Typography>}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                    {c.category && <Chip size="small" label={c.category} variant="outlined" />}
                    {c.level && <Chip size="small" label={c.level} variant="outlined" />}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                  <Button size="small" variant="outlined" onClick={() => { fetchCourseDetail(c.id); setSelectedVideo(null); }}>View</Button>
                  <Button size="small" variant="contained" onClick={() => handleEnroll(c.id)} sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}>Enroll</Button>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      )}

      {tab === 'my-courses' && (
        <Box>
          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
          {!loading && enrollments.length === 0 && (
            <Card sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
              <MenuBookIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary" variant="body1">You haven&apos;t enrolled in any course yet.</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Switch to <strong>Available</strong> to browse and enroll.</Typography>
            </Card>
          )}
          {!loading && enrollments.map((e) => {
            const c = e.course || {};
            const prog = e.progress_percentage ?? 0;
            return (
              <Card key={e.id} sx={{ p: 2.5, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={600}>{c.title}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={e.status} color={e.status === 'completed' ? 'success' : 'primary'} />
                      <Typography variant="body2" color="text.secondary">Progress: {prog}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={prog} sx={{ mt: 1, height: 6, borderRadius: 3, maxWidth: 200 }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    {e.certificate_issued && e.certificate_url && (
                      <Button size="small" startIcon={<CardMembershipIcon />} onClick={() => handleDownloadCertificate(e.certificate_url)}>Certificate</Button>
                    )}
                    <Button size="small" variant="contained" onClick={() => { fetchCourseDetail(c.id); setSelectedVideo(null); }} sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}>Open</Button>
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>
      )}

      {courseDetail && (
        <Card sx={{ p: 3, mt: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 24px rgba(15,118,110,0.08)', borderTop: '3px solid #0f766e' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f766e', mb: 1 }}>{courseDetail.title}</Typography>
          {courseDetail.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{courseDetail.description}</Typography>}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight={500}>Progress</Typography>
              <Typography variant="body2" color="text.secondary">{progress}% · {courseDetail.completedVideos ?? 0}/{courseDetail.totalVideos ?? 0} videos</Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5, bgcolor: alpha('#0f766e', 0.12), '& .MuiLinearProgress-bar': { bgcolor: '#0f766e' } }} />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <VideoLibraryIcon fontSize="small" /> Videos
          </Typography>
          {(courseDetail.chapters || []).map((ch) => (
            <Box key={ch.id} sx={{ mb: 2.5 }}>
              <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>{ch.title}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(ch.videos || []).map((v) => (
                  <Paper key={v.id} elevation={0} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: v.completed ? alpha('#0f766e', 0.06) : 'grey.50' }}>
                    <Button size="small" startIcon={<PlayCircleFilledIcon />} variant="contained" onClick={() => loadVideoDetail(courseDetail.id, { ...v, chapterTitle: ch.title })} sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}>Play</Button>
                    <Typography variant="body2" sx={{ flex: 1 }}>{v.title}</Typography>
                    {v.hasQuiz && !v.quizPassed && !v.completed && (
                      <Chip size="small" label="Quiz required" color="warning" variant="outlined" />
                    )}
                    {v.completed ? <CheckCircleIcon color="success" fontSize="small" /> : (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={v.hasQuiz && !v.quizPassed}
                        onClick={() => handleMarkComplete(courseDetail.id, v.id)}
                      >
                        Mark complete
                      </Button>
                    )}
                  </Paper>
                ))}
              </Box>
            </Box>
          ))}

          {is100 && !isCompleted && (
            <Paper elevation={0} sx={{ mt: 3, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: alpha('#0f766e', 0.04) }}>
              {assessmentError && <Typography variant="body2" color="error" sx={{ mb: 2 }}>{assessmentError}</Typography>}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                {courseDetail.has_assessment ? (
                  <>
                    <Button variant="contained" startIcon={<EmojiEventsIcon />} onClick={handleStartAssessment} disabled={assessmentLoading} sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}>
                      {assessmentLoading ? <CircularProgress size={20} color="inherit" /> : 'Take Assessment (Get Certificate)'}
                    </Button>
                    <Button variant="outlined" onClick={() => setCompleteWithoutModal(true)} disabled={submitting}>Complete without certificate</Button>
                  </>
                ) : (
                  <Button variant="contained" startIcon={<CardMembershipIcon />} onClick={handleCompleteCourse} disabled={submitting} sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}>
                    {submitting ? <CircularProgress size={20} color="inherit" /> : 'Complete course & get certificate'}
                  </Button>
                )}
              </Box>
            </Paper>
          )}

          {isCompleted && courseDetail.enrollment?.certificate_url && (
            <Button variant="contained" startIcon={<CardMembershipIcon />} sx={{ mt: 3, bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }} onClick={() => handleDownloadCertificate(courseDetail.enrollment.certificate_url)}>Download Certificate</Button>
          )}

          <Button variant="text" sx={{ mt: 3, display: 'block' }} onClick={() => { setCourseDetail(null); setSelectedVideo(null); setAssessmentError(''); }}>← Back to list</Button>
        </Card>
      )}

      {selectedVideo && selectedVideo.embedUrl && (
        <Dialog open fullWidth maxWidth="md" onClose={() => setSelectedVideo(null)} PaperProps={{ sx: { borderRadius: 2 } }}>
          <DialogTitle>{selectedVideo.title}</DialogTitle>
          <DialogContent>
            <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', bgcolor: '#000', borderRadius: 1, mb: 2 }}>
              {/\.(mp4|webm|ogg|m3u8)(\?|$)/i.test(selectedVideo.embedUrl) ? (
                <video key={selectedVideo.embedUrl} src={selectedVideo.embedUrl} controls style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} title={selectedVideo.title} />
              ) : (
                <iframe title={selectedVideo.title} src={selectedVideo.embedUrl} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} frameBorder="0" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen />
              )}
            </Box>
            {videoDetailLoading && <CircularProgress size={24} />}
            {videoSnack && <Typography variant="body2" color={videoQuizPassed ? 'success.main' : 'error'} sx={{ mb: 2 }}>{videoSnack}</Typography>}
            {!videoDetailLoading && videoQuestions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Video quiz (pass {videoPassingScore}% to mark complete)</Typography>
                {videoQuestions.map((q, i) => (
                  <FormControl key={q.id} component="fieldset" sx={{ display: 'block', mb: 2 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {i + 1}. {q.question_text}
                      {q.time_in_video_seconds != null && (
                        <Typography component="span" variant="caption" color="text.secondary"> — checkpoint at {Math.floor(q.time_in_video_seconds / 60)}:{String(q.time_in_video_seconds % 60).padStart(2, '0')}</Typography>
                      )}
                    </Typography>
                    <RadioGroup
                      value={videoQuizAnswers[q.id] || ''}
                      onChange={(e) => setVideoQuizAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                    >
                      {(q.options || []).map((o) => (
                        <FormControlLabel key={o.id} value={String(o.id)} control={<Radio />} label={o.option_text} disabled={videoQuizPassed} />
                      ))}
                    </RadioGroup>
                  </FormControl>
                ))}
                {videoQuizScore != null && (
                  <Typography variant="body2" sx={{ mb: 1 }}>Last score: {videoQuizScore}% {videoQuizPassed ? '(passed)' : '(not passed)'}</Typography>
                )}
                {!videoQuizPassed && (
                  <Button variant="contained" onClick={handleSubmitVideoQuiz} disabled={videoQuizSubmitting} sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}>
                    {videoQuizSubmitting ? 'Submitting…' : 'Submit quiz'}
                  </Button>
                )}
              </Box>
            )}
            {!selectedVideo.completed && (
              <Button
                variant="outlined"
                fullWidth
                disabled={(videoQuestions.length > 0 && !videoQuizPassed) || videoDetailLoading}
                onClick={() => courseDetail && handleMarkComplete(courseDetail.id, selectedVideo.id)}
              >
                Mark video complete
              </Button>
            )}
            {selectedVideo.completed && (
              <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>This video is completed.</Typography>
            )}
          </DialogContent>
          <DialogActions><Button onClick={() => setSelectedVideo(null)}>Close</Button></DialogActions>
        </Dialog>
      )}

      <Dialog open={assessmentOpen} onClose={() => { if (!submitting) { setAssessmentOpen(false); setAssessmentError(''); } }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>Assessment</DialogTitle>
        <DialogContent>
          {assessmentError && <Typography color="error" sx={{ mb: 2 }}>{assessmentError}</Typography>}
          {assessmentQuestions.map((q) => (
            <FormControl key={q.id} component="fieldset" sx={{ display: 'block', mb: 3 }}>
              <Typography variant="body1" gutterBottom fontWeight={500}>{q.question_text}</Typography>
              <RadioGroup value={assessmentAnswers[q.id] || ''} onChange={(e) => setAssessmentAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}>
                {(q.options || []).map((o) => (
                  <FormControlLabel key={o.id} value={String(o.id)} control={<Radio />} label={o.option_text} />
                ))}
              </RadioGroup>
            </FormControl>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setAssessmentOpen(false); setAssessmentError(''); }} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitAssessment} disabled={submitting || assessmentQuestions.length === 0} sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}>{submitting ? 'Submitting...' : 'Submit'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resultOpen} onClose={() => setResultOpen(false)} PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>Result</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Typography variant="h4" fontWeight={700} color={result.passed ? 'success.main' : 'error.main'}>{result.score}%</Typography>
            <Typography sx={{ mt: 1 }}>{result.passed ? 'You passed! Congratulations.' : 'You did not pass. You can retry later.'}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          {result.certificate_url && <Button startIcon={<CardMembershipIcon />} onClick={() => handleDownloadCertificate(result.certificate_url)}>Download Certificate</Button>}
          <Button onClick={() => setResultOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={completeWithoutModal} onClose={() => !submitting && setCompleteWithoutModal(false)} PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>Complete without assessment?</DialogTitle>
        <DialogContent><Typography>You will not receive a certificate for this course.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteWithoutModal(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleCompleteCourse} disabled={submitting}>Complete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CandidateCourses;
